<template>
    <Dialog
        :value="true"
        :header-text=" $t('Tax')
                        + (taxCopy.source_text ? ' (' + taxCopy.source_text + ')' : '')
                        + (taxCopy.status_text ? ' - ' + taxCopy.status_text : '')
                        + (taxCopy.visible_text ? ' - ' + taxCopy.visible_text : '')"
        persistent
        :width="800"
        @close_modal="$emit('close')"
    >
        <v-row class="px-4 pb-4">

            <v-col class="pb-2" cols="8">
                <text-field
                    :value="taxCopy.description"
                    :label="getFieldLabel({title: $t('Description'), required: true})"
                    :disabled="!!(taxCopy && taxCopy.ext_id)"
                    :error-messages="getFieldError('description')"
                    @input="taxCopy.description = $event"
                />
            </v-col>
            <v-col class="pb-2" cols="4">
                <text-field
                    :value="taxCopy.percent"
                    :label="getFieldLabel({title: $t('Percent'), required: false})"
                    :disabled="!!(taxCopy && taxCopy.ext_id)"
                    :error-messages="getFieldError('percent')"
                    @input="taxCopy.percent = $event ? $event.replace(',','.') : ''"
                />
            </v-col>

            <v-col class="pb-2" cols="8">
                <text-field
                    :value="taxCopy.ext_id"
                    :label="getFieldLabel({title: $t('Integration Id'), required: false})"
                    :disabled="!!(taxCopy && taxCopy.ext_id)"
                    :error-messages="getFieldError('ext_id')"
                    @input="taxCopy.ext_id = $event"
                />
            </v-col>
            <v-col class="pb-2" cols="4">
                <text-field
                    :value="taxCopy.ext_code"
                    :label="getFieldLabel({title: $t('Integration Code'), required: false})"
                    :disabled="!!(taxCopy && taxCopy.ext_id)"
                    :error-messages="getFieldError('ext_code')"
                    @input="taxCopy.ext_code = $event"
                />
            </v-col>

            <v-col class="pb-2" cols="8">
                <text-field
                    :value="taxCopy.name"
                    :label="getFieldLabel({title: $t('Name on invoice'), required: false})"
                    :error-messages="getFieldError('name')"
                    @input="taxCopy.name = $event"
                />
            </v-col>
            <v-col class="pb-2" cols="4"></v-col>

        </v-row>

        <template slot="footer">
            <danger-button
                :hidden="!taxCopy.is_hidden || taxCopy.status === 'active'"
                @click="onRestoreTax()">
                {{ $t("Restore") }}
            </danger-button>
            <danger-button
                :hidden="!!(taxCopy && taxCopy.source) || taxCopy.status === 'inactive'"
                @click="onArchiveTax()">
                {{ $t("Archive") }}
            </danger-button>
            <danger-button
                :hidden="!!taxCopy.is_hidden"
                @click="onHideTax()">
                {{ $t("Hide") }}
            </danger-button>

            <v-spacer/>
            <secondary-button @click="$emit('close')">
                {{ $t("Cancel") }}
            </secondary-button>
            <primary-button @click="onSaveTax(taxCopy)">
                {{ $t("Save") }}
            </primary-button>
        </template>
    </Dialog>
</template>

<script>
import Dialog from "../Dialog.vue";
import {computed, reactive, ref} from "@vue/composition-api";
import {$t} from "@/i18n";
import {get} from '@/helpers/get-set'
import Loader from "@/utility/loader";
import AccountModule from "@/store/modules/account";
import RightsModule from "@/composables/use-rights";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import ApiTax from "../../api/billing/tax";
import DialogModule from "../../store/modules/dialog";

export default {
    name: "TaxDialog",
    components: {
        Dialog
    },
    props: {
        companyId: {
            type: Number,
            required: true
        },
        taxId: {
            type: Number,
            required: false
        },
        tax: {
            type: Object,
            required: false
        },
        errors: {
            type: Array,
            default: null,
            required: false
        },
    },
    setup(props, ctx) {
        const rightsModule = RightsModule()
        const {current_account} = AccountModule();
        const {can_edit_account} = RightsModule();
        const loader = reactive(new Loader())
        const dialogModule = DialogModule()

        const taxCopy = ref(null);
        taxCopy.value = {...props.tax}

        const getFieldLabel = (item) => {
            let value = item.title
            if (item.required) {
                value += ' *'
            }
            return value
        }
        const getFieldError = (name) => {
            if (!props.errors) return null
            let value = props.errors.find(item => {
                if (item.name === name) return true
                let split = name.split('.')
                return split.length > 1 && split[1] === item.name;
            })
            if (!value) return null
            return value.error_message
        }
        const getFieldValue = (name, isSwitch = false) => {
            let value = get(taxCopy.value, name)
            if (isSwitch && !value) {
                return false
            }
            return value
        }
        const setFieldValue = (item, value) => {
            let split = item.name.split('.')
            if (split.length > 1) {
                taxCopy.value[split[0]][split[1]] = value
                // ctx.emit('clear-error', split[1])
            } else {
                taxCopy.value[item.name] = value
                // ctx.emit('clear-error', item.name)
            }
            if (item.onSelect) {
                item.onSelect(value)
            }
        }

        const onSaveTax = async (payload) => {
            if (payload && !payload.percent && !payload.name && !payload.description) return
            try {
                await ApiTax.saveTax(props.companyId, props.taxId, payload)
                ctx.emit('close')
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const onHideTax = () => {
            dialogModule.setConfirmOptions({
                title: $t("Are you sure?"),
                confirm_text: $t("Hide"),
                cancel_text: $t("Cancel"),
                action: () => hideTax(),
            })
            dialogModule.openConfirmDialog()
        }
        const hideTax = async () => {
            try {
                await ApiTax.hideTax(props.companyId, props.taxId)
                ctx.emit('close')
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const onArchiveTax = () => {
            dialogModule.setConfirmOptions({
                title: $t("Are you sure?"),
                confirm_text: $t("Archive"),
                cancel_text: $t("Cancel"),
                action: () => archiveTax(),
            })
            dialogModule.openConfirmDialog()
        }
        const archiveTax = async () => {
            try {
                await ApiTax.archiveTax(props.companyId, props.taxId)
                ctx.emit('close')
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const onRestoreTax = () => {
            dialogModule.setConfirmOptions({
                title: $t("Are you sure?"),
                confirm_text: $t("Restore"),
                cancel_text: $t("Cancel"),
                action: () => restoreTax(),
            })
            dialogModule.openConfirmDialog()
        }
        const restoreTax = async () => {
            try {
                await ApiTax.restoreTax(props.companyId, props.taxId)
                ctx.emit('close')
            } catch (e) {
                errorDialog.showError(e)
            }
        }


        return {
            taxCopy,
            getFieldValue,
            setFieldValue,
            getFieldError,
            getFieldLabel,
            onSaveTax,
            onHideTax,
            onArchiveTax,
            onRestoreTax,
        }
    }
};
</script>